import { RouterModule, Routes } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { NgModule } from '@angular/core';
import { DatabaseComponent } from './database.component';
import { PeopleComponent } from './pages/people/people.component';
import { DoorsComponent } from './pages/doors/doors.component';
import { CustomEventsComponent } from './pages/custom-events/custom-events.component';
import { CustomEventsEditComponent } from './pages/custom-events/custom-events-edit/custom-events-edit.component';
import { VehiclesComponent } from './pages/vehicles/vehicles.component';
import { OrgGroupsComponent } from './pages/org-groups/org-groups.component';
import { OrgGroupComponent } from './pages/org-groups/components/org-group/org-group.component';
import { RouterPermissionGuard } from '../../guards/router-permission-guard';
import { DeveloperGuard } from '../../guards/developer.guard';
import { PermissionModel } from '@models/permission.model';
import { YoutubeComponent } from './pages/youtube/youtube.component';
import { EntrancesComponent } from './pages/entrances/entrances.component';
import { UniformComponent } from './pages/uniform/uniform.component';
import { CustomObjectsComponent } from './pages/custom-objects/custom-objects.component';
import { VariablesComponent } from './pages/variables/variables.component';
import { TagsComponent } from './pages/tags/tags.component';
import { EntitiesFilteredByTagComponent } from './pages/tags/pages/entities-filtered-by-tag/entities-filtered-by-tag.component';
import { ShelfsComponent } from './pages/shelfs/shelfs.component';
import { OrganizationCustomObjectsMapComponent } from '../settings/pages/custom-objects-map/organization-custom-objects-map.component';
import { ContactsComponent } from './pages/contacts/contacts.component';
import { ZoneRegionsComponent } from './pages/zone-regions/zone-regions.component';
import { WebhooksComponent } from './pages/workflow/pages/webhooks/webhooks.component';
import { CategoriesComponent } from './pages/categories/categories.component';
import { PersonsComponent } from './pages/person/pages/persons/persons.component';
import { PersonGroupsComponent } from './pages/person/pages/person-groups/person-groups.component';
import { PersonGroupDetailsComponent } from './pages/person/pages/person-group-details/person-group-details.component';
import { VehicleGroupsComponent } from './pages/vehicles/pages/vehicle-groups/vehicle-groups.component';
import { VehicleGroupDetailsComponent } from './pages/vehicles/pages/vehicle-group-details/vehicle-group-details.component';

export const databaseRoutes: Routes = [
  {
    path: '',
    component: DatabaseComponent,
    children: [
      {
        path: routerSegments.persons,
        loadComponent: () => PersonsComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabasePeopleDirectory,
          ],
        },
      },
      {
        path: `${routerSegments.personGroups}/:groupId`,
        loadComponent: () => PersonGroupDetailsComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabasePeopleDirectory,
          ],
        },
      },
      {
        path: routerSegments.personGroups,
        loadComponent: () => PersonGroupsComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabasePeopleDirectory,
          ],
        },
      },
      {
        path: routerSegments.doors,
        component: DoorsComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabaseDoorsDirectory,
          ],
        },
      },
      {
        path: routerSegments.vehicles,
        loadComponent: () => VehiclesComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabaseVehiclesDirectory,
          ],
        },
      },
      {
        path: `${routerSegments.vehicleGroups}/:groupId`,
        loadComponent: () => VehicleGroupDetailsComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabaseVehiclesDirectory,
          ],
        },
      },
      {
        path: routerSegments.vehicleGroups,
        loadComponent: () => VehicleGroupsComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabaseVehiclesDirectory,
          ],
        },
      },
      {
        path: `${routerSegments.groups}/:groupType`,
        loadComponent: () => OrgGroupsComponent,
        data: {
          reuseRoute: false,
        },
      },
      {
        path: `${routerSegments.groups}/:groupType/:groupId`,
        component: OrgGroupComponent,
      },
      {
        path: routerSegments.eventTypes,
        children: [
          {
            path: `${routerSegments.edit}/:id`,
            loadComponent: () => CustomEventsEditComponent,
          },
          {
            path: routerSegments.create,
            loadComponent: () => CustomEventsEditComponent,
          },
          {
            path: '',
            component: CustomEventsComponent,
          },
        ],
      },
      {
        path: routerSegments.youtube,
        component: YoutubeComponent,
      },
      {
        path: routerSegments.entrances,
        component: EntrancesComponent,
      },
      {
        path: routerSegments.variables,
        loadComponent: () => VariablesComponent,
      },
      {
        path: routerSegments.uniform,
        component: UniformComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabasePeopleDirectory,
          ],
        },
      },
      {
        path: routerSegments.customObjects,
        component: CustomObjectsComponent,
        canActivate: [RouterPermissionGuard],
        data: {
          permissions: [
            PermissionModel.Permissions.OrganizationDatabasePeopleDirectory,
          ],
        },
      },
      {
        path: routerSegments.tags,
        loadComponent: () => TagsComponent,
      },
      {
        path: routerSegments.contacts,
        loadComponent: () => ContactsComponent,
      },
      {
        path: routerSegments.contactGroups,
        canActivate: [DeveloperGuard],
        children: [
          {
            path: ':contactGroupId',
            loadComponent: () =>
              import(
                './pages/contact-groups/contact-group-detail/contact-group-detail.component'
              ).then((m) => m.ContactGroupDetailComponent),
          },
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/contact-groups/contact-groups.component'
              ).then((m) => m.ContactGroupsComponent),
          },
        ],
      },
      {
        path: routerSegments.shelfs,
        loadComponent: () => ShelfsComponent,
        data: {
          permissions: [PermissionModel.Permissions.OrganizationDatabase],
        },
      },
      {
        path: routerSegments.zoneRegions,
        children: [
          {
            path: ':zoneRegionId',
            loadComponent: () =>
              import('./pages/zone-regions/zone-region-detail/zone-region-detail.component').then(
                (m) => m.ZoneRegionDetailComponent,
              ),
            data: { permissions: [PermissionModel.Permissions.OrganizationDatabase] },
          },
          {
            path: '',
            loadComponent: () => ZoneRegionsComponent,
            data: { permissions: [PermissionModel.Permissions.OrganizationDatabase] },
          },
        ],
      },
      {
        path: routerSegments.workflowWebhooks,
        loadComponent: () => WebhooksComponent,
      },
      {
        path: routerSegments.categories,
        loadComponent: () => CategoriesComponent,
      },
      {
        path: `${routerSegments.tags}/:tagId`,
        loadComponent: () => EntitiesFilteredByTagComponent,
      },
      {
        path: routerSegments.mapObjects,
        loadComponent: () => OrganizationCustomObjectsMapComponent,
      },
      {
        path: `${routerSegments.mapObjects}/:id`,
        loadComponent: () => OrganizationCustomObjectsMapComponent,
      },
      {
        path: routerSegments.schedule,
        loadChildren: () =>
          import('./pages/time-schedule/time-schedule.module').then(
            (m) => m.TimeScheduleModule,
          ),
      },
      {
        path: '',
        redirectTo: routerSegments.persons,
        pathMatch: 'full',
      },
    ],
  },
];
